import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { formsApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, Clock, Eye, Mail, RefreshCw, AlertTriangle,
  FileCheck2, Search, Send, Ban, Trash2, RefreshCcw,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

const STATUS_BADGES = {
  sent: { label: 'Sent', class: 'bg-sky-100 text-sky-700 border-sky-200' },
  opened: { label: 'Opened', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  in_progress: { label: 'In Progress', class: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', class: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  expired: { label: 'Expired', class: 'bg-rose-100 text-rose-700 border-rose-200' },
  cancelled: { label: 'Cancelled', class: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function FormSubmissionsPage() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getTenantPath, tenant, user } = useAuth();
  const featureEnabled = !!tenant?.settings?.enable_online_forms;
  const isAdmin = user?.role === 'admin' || user?.is_master_admin;

  const [submissions, setSubmissions] = useState([]);
  const [allForms, setAllForms] = useState([]); // for the form filter dropdown
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [formFilter, setFormFilter] = useState(formId || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busyAction, setBusyAction] = useState(null); // 'resend'|'cancel'|'expire'|'delete'|'resync:{id}'
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Form filter is always honoured from local state — if a formId is
      // in the URL it locks the filter to that form. Status filter is
      // applied client-side so we can do counts even with 'all'.
      const params = {};
      const activeFormId = formId || (formFilter !== 'all' ? formFilter : null);
      if (activeFormId) params.form_id = activeFormId;

      const subsRes = await formsApi.listSubmissions(params);
      setSubmissions(subsRes.data || []);

      // Lazy-load the forms list once for the filter dropdown.
      if (!allForms.length) {
        const fr = await formsApi.list();
        setAllForms(fr.data || []);
      }
      // If we're on /forms/:id/submissions, also fetch the form for the title.
      if (formId) {
        const fmRes = await formsApi.get(formId);
        setForm(fmRes.data);
      } else {
        setForm(null);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load submissions'));
    } finally {
      setLoading(false);
    }
  }, [formId, formFilter, allForms.length]);

  useEffect(() => {
    if (!featureEnabled) {
      navigate(getTenantPath('/forms'));
      return;
    }
    load();
    // Clear selection whenever the dataset changes.
    setSelectedIds(new Set());
  }, [featureEnabled, load, navigate, getTenantPath]);

  // Persist the status filter in the URL so refresh / back-button works.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter === 'all') next.delete('status');
    else next.set('status', statusFilter);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fieldLabel = (fieldId) => {
    return form?.fields?.find((f) => f.id === fieldId)?.label || fieldId;
  };

  // Client-side filtering — status + free-text search across candidate
  // name / email / form name. Keeping this in the browser lets the
  // status pill counts in the toolbar reflect the *full* loaded set
  // even when a filter is active.
  const filteredSubmissions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (submissions || []).filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (s.candidate_name || '').toLowerCase().includes(q) ||
        (s.candidate_email || '').toLowerCase().includes(q) ||
        (s.form_name || '').toLowerCase().includes(q)
      );
    });
  }, [submissions, statusFilter, searchQuery]);

  const statusCounts = useMemo(() => {
    const out = { sent: 0, opened: 0, in_progress: 0, completed: 0, expired: 0, cancelled: 0 };
    (submissions || []).forEach((s) => { if (out[s.status] != null) out[s.status]++; });
    return out;
  }, [submissions]);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredSubmissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredSubmissions.map((s) => s.id)));
    }
  };

  // Bulk actions reuse the per-row endpoint behind a single batch call.
  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    // Soft client-side warning for irreversible delete.
    if (action === 'delete' && !window.confirm(`Permanently delete ${ids.length} submission${ids.length === 1 ? '' : 's'}? Bullhorn files already uploaded are unaffected.`)) {
      return;
    }
    setBusyAction(action);
    try {
      const { data } = await formsApi.bulkSubmissions(action, ids);
      const verb = { resend: 'Reminded', cancel: 'Cancelled', expire: 'Expired', delete: 'Deleted' }[action] || 'Processed';
      const parts = [`${verb} ${data.ok}`];
      if (data.skipped) parts.push(`skipped ${data.skipped}`);
      if (data.failed) parts.push(`failed ${data.failed}`);
      toast.success(parts.join(' · '));
      setSelectedIds(new Set());
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, `Bulk ${action} failed`));
    } finally {
      setBusyAction(null);
    }
  };

  const canBulkOn = (predicate) => Array.from(selectedIds).every((id) => {
    const s = submissions.find((x) => x.id === id);
    return s && predicate(s);
  });
  const canBulkResend = canBulkOn((s) => !['completed', 'expired', 'cancelled'].includes(s.status));
  const canBulkCancel = canBulkOn((s) => s.status !== 'completed');
  const canBulkExpire = canBulkOn((s) => !['completed', 'cancelled', 'expired'].includes(s.status));

  const allSelected = filteredSubmissions.length > 0 && selectedIds.size === filteredSubmissions.length;

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto" data-testid="form-submissions-page">
        {formId && (
          <div className="flex items-center gap-3 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(getTenantPath('/forms'))} className="gap-1.5" data-testid="form-submissions-back-btn">
              <ArrowLeft className="w-4 h-4" /> Forms
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl text-foreground tracking-tight">
              {form ? `Tracking · ${form.name}` : 'Form Tracking'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredSubmissions.length} of {submissions.length} submission{submissions.length === 1 ? '' : 's'} shown
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by candidate, email, or form name…"
              className="pl-9"
              data-testid="form-submissions-search-input"
            />
          </div>
          {!formId && (
            <Select value={formFilter} onValueChange={setFormFilter}>
              <SelectTrigger className="w-[200px]" data-testid="form-submissions-form-filter">
                <SelectValue placeholder="All forms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All forms</SelectItem>
                {allForms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="form-submissions-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="sent">Sent ({statusCounts.sent})</SelectItem>
              <SelectItem value="opened">Opened ({statusCounts.opened})</SelectItem>
              <SelectItem value="in_progress">In Progress ({statusCounts.in_progress})</SelectItem>
              <SelectItem value="completed">Completed ({statusCounts.completed})</SelectItem>
              <SelectItem value="expired">Expired ({statusCounts.expired})</SelectItem>
              <SelectItem value="cancelled">Cancelled ({statusCounts.cancelled})</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-1.5" data-testid="form-submissions-refresh-btn">
            <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between p-4 mb-4 bg-primary/5 rounded-lg border border-primary/20" data-testid="form-submissions-bulk-bar">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium">
                {selectedIds.size} submission{selectedIds.size > 1 ? 's' : ''} selected
              </span>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => runBulk('resend')}
                disabled={busyAction === 'resend' || !canBulkResend}
                title={!canBulkResend ? 'Completed / expired / cancelled submissions cannot be reminded.' : ''}
                data-testid="form-submissions-bulk-resend-btn"
              >
                {busyAction === 'resend' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                Send Reminders
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => runBulk('expire')}
                disabled={busyAction === 'expire' || !canBulkExpire}
                data-testid="form-submissions-bulk-expire-btn"
              >
                {busyAction === 'expire' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Expire
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => runBulk('cancel')}
                disabled={busyAction === 'cancel' || !canBulkCancel}
                data-testid="form-submissions-bulk-cancel-btn"
              >
                {busyAction === 'cancel' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Ban className="w-4 h-4 mr-1" />}
                Cancel
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-700 border-red-400 hover:bg-red-50 hover:text-red-800"
                  onClick={() => runBulk('delete')}
                  disabled={busyAction === 'delete'}
                  data-testid="form-submissions-bulk-delete-btn"
                >
                  {busyAction === 'delete' ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                  Delete
                </Button>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-muted-foreground">Loading…</div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-white p-16 text-center" data-testid="form-submissions-empty">
            <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <h3 className="font-heading text-xl text-foreground mb-1">
              {submissions.length === 0 ? 'No submissions yet' : 'No matches'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {submissions.length === 0
                ? 'Send a form to a candidate to see results here.'
                : 'Try adjusting the search or filters above.'}
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-border">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={toggleSelectAll}
                      data-testid="form-submissions-select-all"
                    />
                  </th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Recipient</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Form</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Status</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Bullhorn</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Sent</th>
                  <th className="text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground px-5 py-3">Completed</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubmissions.map((s) => {
                  const badge = STATUS_BADGES[s.status] || STATUS_BADGES.sent;
                  const canResend = !['completed', 'expired', 'cancelled'].includes(s.status);
                  const checked = selectedIds.has(s.id);
                  return (
                    <tr
                      key={s.id}
                      className={`border-b border-border last:border-b-0 hover:bg-slate-50/50 ${checked ? 'bg-primary/5' : ''}`}
                      data-testid={`form-submission-row-${s.id}`}
                    >
                      <td className="px-3 py-3">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleSelect(s.id)}
                          data-testid={`form-submission-select-${s.id}`}
                        />
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-sm">{s.candidate_name}</div>
                        <div className="text-xs text-muted-foreground">{s.candidate_email}</div>
                      </td>
                      <td className="px-5 py-3 text-sm">{s.form_name}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${badge.class}`}>
                          {badge.label}
                        </span>
                        {s.reminder_count > 0 && (
                          <div className="text-[10px] text-muted-foreground mt-1" title={s.last_reminded_at ? `Last reminded ${formatDistanceToNow(new Date(s.last_reminded_at), { addSuffix: true })}` : ''}>
                            Reminded {s.reminder_count}×
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <BullhornSyncBadge submission={s} />
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground">
                        {s.sent_at ? format(new Date(s.sent_at), 'MMM d, yyyy HH:mm') : '—'}
                      </td>
                      <td className="px-5 py-3 text-xs text-muted-foreground">
                        {s.completed_at ? format(new Date(s.completed_at), 'MMM d, yyyy HH:mm') : '—'}
                      </td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setViewing(s)}
                          className="gap-1.5"
                          data-testid={`form-submission-view-${s.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </Button>
                        {canResend && (
                          <ResendButton submission={s} onResent={load} />
                        )}
                        {s.status === 'completed' && (
                          <ResyncButton submission={s} onSynced={load} />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Submission detail */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="form-submission-detail-dialog">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{viewing.form_name}</DialogTitle>
                <DialogDescription>Submitted field values for this recipient.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="text-sm">
                  <span className="text-muted-foreground">Recipient: </span>
                  <strong>{viewing.candidate_name}</strong> <span className="text-muted-foreground">({viewing.candidate_email})</span>
                </div>
                {viewing.field_values?.length ? (
                  <div className="space-y-2 mt-4">
                    {viewing.field_values.map((fv, i) => (
                      <div key={fv.field_id || `fv-${i}`} className="border-b border-border last:border-b-0 pb-2">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">{fieldLabel(fv.field_id)}</p>
                        <p className="text-sm mt-0.5 break-words">
                          {fv.value == null || fv.value === ''
                            ? <span className="text-muted-foreground italic">—</span>
                            : Array.isArray(fv.value) ? fv.value.join(', ')
                            : typeof fv.value === 'string' && fv.value.startsWith('data:image')
                              ? <img src={fv.value} alt="signature" className="h-20 border border-border rounded" />
                              : String(fv.value)}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">No values submitted yet.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

/**
 * Compact Bullhorn-sync badge — surfaces whether the completed
 * submission has been written back + uploaded to the Files tab.
 */
function BullhornSyncBadge({ submission }) {
  if (submission.status !== 'completed') {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const status = submission.bullhorn_writeback_status || 'pending';
  const fileId = submission.bullhorn_file_id;
  const map = {
    success: { label: fileId ? 'Synced' : 'Sync OK', class: 'bg-emerald-100 text-emerald-700 border-emerald-200', Icon: FileCheck2 },
    partial: { label: 'Partial', class: 'bg-amber-100 text-amber-700 border-amber-200', Icon: AlertTriangle },
    failed: { label: 'Failed', class: 'bg-rose-100 text-rose-700 border-rose-200', Icon: AlertTriangle },
    pending: { label: 'Pending', class: 'bg-slate-100 text-slate-600 border-slate-200', Icon: Clock },
  };
  const cfg = map[status] || map.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${cfg.class}`}
      title={submission.bullhorn_writeback_error || cfg.label}
      data-testid={`form-submission-bh-badge-${submission.id}`}
    >
      <cfg.Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

/**
 * Per-row "Remind" trigger — calls `/api/forms/submissions/{id}/resend`
 * to re-email a candidate whose submission is still pending.
 */
function ResendButton({ submission, onResent }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await formsApi.resendSubmission(submission.id);
      toast.success('Reminder sent');
      onResent?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Reminder failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handle}
      disabled={busy}
      className="gap-1.5"
      data-testid={`form-submission-resend-${submission.id}`}
      title="Re-send reminder email"
    >
      <Send className={`w-3.5 h-3.5 ${busy ? 'animate-pulse' : ''}`} />
      {busy ? 'Sending…' : 'Remind'}
    </Button>
  );
}

/**
 * Manual re-sync trigger — calls `/api/forms/submissions/{id}/resync`
 * so the recruiter can retry a failed Bullhorn write-back without
 * re-asking the candidate.
 */
function ResyncButton({ submission, onSynced }) {
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await formsApi.resyncSubmission(submission.id);
      const status = data?.status || 'unknown';
      if (status === 'success') toast.success('Re-synced to Bullhorn');
      else if (status === 'partial') toast.warning('Partial sync — some steps failed');
      else if (status === 'failed') toast.error(data?.write_back?.error || data?.upload?.error || 'Re-sync failed');
      else toast.info(`Re-sync result: ${status}`);
      onSynced?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Re-sync failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handle}
      disabled={busy}
      className="gap-1.5"
      data-testid={`form-submission-resync-${submission.id}`}
      title="Re-send to Bullhorn"
    >
      <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Syncing…' : 'Re-sync'}
    </Button>
  );
}
